import posthog from 'posthog-js';

/**
 * What triggered the sign-in — recorded on `auth_completed` so every signup is
 * attributable to the surface that asked for it. One union, one capture point:
 * every surface signs in through `InlineAuth`, which reports through
 * `captureAuthCompleted`, so a new surface can't invent a shape of its own.
 */
export type AuthSource =
  /** The storefront's cards. */
  | 'quiz_answers'
  | 'product_card'
  | 'calls_block'
  | 'navbar'
  /** The main page's first ghost card — the only ask on a signed-out `/`,
      since its masthead became a fact rather than a request. */
  | 'home_ghost'
  /** The `/create` page's own constructor: an ad landing, nobody's recruit,
      so no `inviteReply` and no `sourceTutor`. */
  | 'quizzes_page'
  /** The prebuilt page's claim on `/create?ig=`: the calls card's one button. */
  | 'prebuilt_page';

/**
 * The steps before the last one. Every card asks for a sign-in through the same
 * component, so instrumenting it once gives each of them the same funnel, told
 * apart by `source` alone:
 *
 *   auth_form_shown → auth_email_submitted → auth_completed
 *
 * On a card that hides the form behind a button (a product card, the sign-in
 * pill) the form mounts on that tap, so `auth_form_shown` IS the tap. On a card
 * that shows the form outright (the calls block, the quiz's score gate) it
 * coincides with the card itself — which is the honest reading: that card's ask
 * is always on screen.
 */
type AuthStep =
  /** The form is on screen — the visitor started signing in. */
  | 'auth_form_shown'
  /** They gave an email and a code was sent. */
  | 'auth_email_submitted'
  /** The code came back wrong — a typo, not a silent abandon. */
  | 'auth_code_rejected';

export function captureAuthStep(
  step: AuthStep,
  params: { source: AuthSource; sourceTutor?: string | null }
) {
  posthog.capture(step, {
    provider_username: params.sourceTutor ?? null,
    creator_username: params.sourceTutor ?? null,
    tutor_username: params.sourceTutor ?? null,
    source: params.source,
  });
}

/**
 * The last step of every acquisition funnel. `is_new_user` is what separates a
 * lead from a returning sign-in, and `source` is what attributes it — a funnel
 * that filters on both is the only way to read one surface's conversion.
 */
export function captureAuthCompleted(params: {
  source: AuthSource;
  method: 'otp' | 'google';
  isNewUser: boolean;
  /** Provider whose page this happened on, when there is one. */
  sourceTutor?: string | null;
}) {
  posthog.capture('auth_completed', {
    provider_username: params.sourceTutor ?? null,
    creator_username: params.sourceTutor ?? null,
    tutor_username: params.sourceTutor ?? null,
    source: params.source,
    method: params.method,
    is_new_user: params.isNewUser,
  });
}
